import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Lock, ArrowLeft } from "lucide-react";
import Logo from "@/components/site/Logo";

export default function AdminLogin() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (user && user.email) navigate("/admin/dashboard", { replace: true });
    }, [user, navigate]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auth/setup-status");
                if (data.needs_setup) navigate("/admin/setup", { replace: true });
            } catch {
                // ignore
            }
        })();
    }, [navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            navigate("/admin/dashboard");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white grid lg:grid-cols-2">
            <div className="hidden lg:block relative bg-[#18181a] border-r border-white/10">
                <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: "url(https://images.pexels.com/photos/33814734/pexels-photo-33814734.jpeg?auto=compress&cs=tinysrgb&w=1200)" }} />
                <div className="absolute inset-0 bg-gradient-to-br from-black/70 to-black/90" />
                <div className="absolute top-0 left-0 w-full h-2 hazard-stripes opacity-80" />
                <div className="relative z-10 h-full flex flex-col justify-between p-12">
                    <Logo variant="compact" />
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d52b1e] mb-2">Admin Console</div>
                        <h1 className="font-display uppercase text-5xl leading-none">Manage your<br />stock yard.</h1>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center p-8">
                <form onSubmit={submit} className="w-full max-w-md bg-[#18181a] border border-white/10 p-8" data-testid="admin-login-form">
                    <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-white mb-8" data-testid="back-to-site-link">
                        <ArrowLeft className="w-3 h-3" /> Back to site
                    </Link>
                    <div className="flex items-center gap-3 mb-8">
                        <div className="bg-[#d52b1e] text-white w-10 h-10 flex items-center justify-center">
                            <Lock className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Sign in</div>
                            <div className="font-display uppercase text-2xl">Admin Access</div>
                        </div>
                    </div>

                    <div className="space-y-7">
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="industrial-input" required data-testid="login-email-input" />
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="industrial-input" required data-testid="login-password-input" />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="mt-8 w-full bg-[#d52b1e] hover:bg-[#f03022] disabled:opacity-60 text-white px-5 py-3 uppercase text-xs tracking-[0.25em] font-bold transition-colors"
                        data-testid="login-submit-btn"
                    >
                        {submitting ? "Signing in..." : "Sign in"}
                    </button>
                    <div className="mt-6 text-center">
                        <Link to="/admin/forgot-password" className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-white" data-testid="forgot-password-link">
                            Forgot password?
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
